import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { useResidentContext } from "../provider/ResidentProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { CreateResidentAdmissionRequest } from "app/services/api/types/residents/requests/CreateResidentAdmissionRequest";
import { admissionTypeOptions } from "app/services/api/types/residents/enums/AdmissionTypeEnums";
import {
  ResidentStatusEnum,
  statusTypeOptions,
} from "app/services/api/types/residents/enums/ResidentStatusEnum";
import { dependencyTypeOptions } from "app/services/api/types/residents/enums/DependencyEnums";
import { ResidentAdmissionForm } from "app/services/api/types/residents/models/ResidentAdmissionForm";
import {
  useDeleteOtherNotesMediaMutation,
  useUpdateOtherNotesMediaMutation,
  useUpdateResidentAdmissionsMutation,
} from "app/services/api/residentsApi";
import { handleBackendError } from "ui/utils/Errors";
import { convertToFile } from "ui/utils/fileUtils";
import { DocumentPickerAsset } from "expo-document-picker";
import {
  useGetAllBedsQuery,
  useGetAllRoomsQuery,
} from "app/services/api/locationsApi";
import { usePageFocus } from "ui/utils/FocusHook";
import { isDocumentPickerAsset } from "ui/types/ThrelsFileType";
import { format } from "date-fns";
import { AttendingForEnum } from "app/services/api/types/residents/enums/AttendingForEnum";

export const useHandleResidentAdmission = () => {
  const { resident, residentId } = useResidentContext();
  const { showNewToast, showErrorToast } = useDisplayToast();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [triggerUpdateOtherNotesMedia] = useUpdateOtherNotesMediaMutation();
  const [triggerNoteDelete] = useDeleteOtherNotesMediaMutation();

  const [triggerUpdateResidentAdmissions] =
    useUpdateResidentAdmissionsMutation();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<ResidentAdmissionForm>({
    defaultValues: {
      admission_start_date: format(new Date(), "yyyy-MM-dd"),
      admission_type: undefined,
      admission_type_details: undefined,
      attending_for: undefined,
      admission_end_date: undefined,
      status: undefined,
      status_reason: undefined,
      dependency: undefined,
      other_notes: undefined,
      bed_id: null,
      other_notes_media: [],
    },
  });

  usePageFocus(() => {
    if (resident) {
      reset({
        admission_start_date: resident.admission_start_date,
        admission_type: resident.admission_type,
        admission_type_details: resident.admission_type_details,
        attending_for: resident.attending_for,
        admission_end_date: resident.admission_end_date
          ? format(new Date(resident.admission_end_date), "yyyy-MM-dd")
          : undefined,
        status: resident.status,
        status_reason: resident.status_reason,
        dependency: resident.dependency,
        other_notes: resident.other_notes,
        room_id: resident.room?.id,
        bed_id: resident.bed?.id,
        other_notes_media: resident.other_notes_media
          ? [resident.other_notes_media]
          : [],
      });
    }
  }, [resident, reset, setValue]);

  const attendingFor = useWatch({
    control,
    name: "attending_for",
    defaultValue: resident?.attending_for,
  });

  const selectedLocationId = useWatch({
    control,
    name: "location_id",
    defaultValue: resident?.location?.id,
  });

  const selectedRoomId = useWatch({
    control,
    name: "room_id",
    defaultValue: resident?.room?.id,
  });

  const selectedStatus = useWatch({
    control,
    name: "status",
    defaultValue: undefined,
  });

  const { data: roomsData, isLoading: areRoomsLoading } = useGetAllRoomsQuery(
    { filter: { location_id: selectedLocationId } },
    {
      skip: !selectedLocationId,
    },
  );

  const { data: bedsData, isLoading: areBedsLoading } = useGetAllBedsQuery(
    { filter: { room_id: selectedRoomId } },
    {
      skip: !selectedRoomId,
    },
  );

  const formattedRoomData = useMemo(
    () =>
      roomsData?.map((room) => ({
        label: room.room_number,
        value: room.id,
      })) || [],
    [roomsData],
  );

  const formattedBedsData = useMemo(
    () =>
      bedsData?.map((beds) => ({
        label: beds.bed_number,
        value: beds.id,
        disabled: !beds.is_available && resident?.bed?.id !== beds.id,
      })) || [],
    [bedsData, resident],
  );

  const prevLocationIdRef = useRef<number | undefined>(selectedLocationId);

  useEffect(() => {
    if (
      prevLocationIdRef.current !== undefined &&
      selectedLocationId !== prevLocationIdRef.current
    ) {
      setValue("room_id", undefined);
      setValue("bed_id", undefined);
    }

    prevLocationIdRef.current = selectedLocationId;
  }, [selectedLocationId, setValue]);

  const prevRoomIdRef = useRef<string | number | undefined>(selectedRoomId);

  useEffect(() => {
    if (
      prevRoomIdRef.current !== undefined &&
      selectedRoomId !== prevRoomIdRef.current
    ) {
      setValue("bed_id", undefined);
    }

    prevRoomIdRef.current = selectedRoomId;
  }, [selectedRoomId, setValue]);

  const validateRoomSelection = (value: unknown) => {
    const needsRoom =
      attendingFor === AttendingForEnum.RESPITE ||
      attendingFor === AttendingForEnum.RESIDENT ||
      (attendingFor === AttendingForEnum.NIGHT_SHELTER && !value);

    if (needsRoom && !value) {
      return "Room selection is required for this admission type";
    }

    if (needsRoom && selectedLocationId && !value) {
      return "Room selection is required when a location is selected";
    }

    return true;
  };

  const validateEndDate = (value: unknown) => {
    if (attendingFor === AttendingForEnum.RESPITE && !value) {
      return "End date is required for Respite";
    } else if (selectedStatus === ResidentStatusEnum.TERMINATED && !value) {
      return "End date is required to discharge a resident";
    }
    return true;
  };

  const updateResidentAdmission = async (
    admissionData: CreateResidentAdmissionRequest,
    residentId: number,
  ) => {
    await triggerUpdateResidentAdmissions({
      residentId,
      body: admissionData,
    }).unwrap();
  };

  const uploadOtherNotesMedia = async (
    image: DocumentPickerAsset[],
    residentId: number,
  ) => {
    if (image && image.length > 0) {
      const files: File[] = convertToFile(image);

      const formData = new FormData();
      files.forEach((file) => {
        formData.append("other_notes_media", file);
      });

      await triggerUpdateOtherNotesMedia({
        residentId,
        body: formData,
      }).unwrap();
    }
  };
  const submitForm = async () => {
    return new Promise<boolean>((resolve) => {
      if (isSubmitting) {
        resolve(false);
        return;
      }
      handleSubmit(
        async (formData: ResidentAdmissionForm) => {
          try {
            if (!residentId) {
              resolve(false);
              return;
            }

            await updateResidentAdmission(
              {
                admission_start_date: formData.admission_start_date!,
                admission_type: formData.admission_type!,
                admission_type_details: formData.admission_type_details!,
                attending_for: formData.attending_for!,
                admission_end_date: formData.admission_end_date!,
                status: formData.status!,
                status_reason: formData.status_reason!,
                dependency: formData.dependency!,
                other_notes: formData.other_notes!,
                bed_id: formData.bed_id!,
              },
              residentId,
            );

            const { other_notes_media: otherNotesMedia } = formData;

            if (
              otherNotesMedia?.length &&
              otherNotesMedia[0] &&
              isDocumentPickerAsset(otherNotesMedia[0])
            ) {
              await uploadOtherNotesMedia([otherNotesMedia[0]], residentId);
            } else if (
              !otherNotesMedia?.length &&
              resident?.other_notes_media?.id
            ) {
              await triggerNoteDelete({
                residentId,
                mediaId: resident.other_notes_media.id,
              }).unwrap();
            }

            showNewToast({
              title: "Resident Admission Details Updated",
              message: "Successfully updated resident's admission details",
              type: "success",
            });
            resolve(true);
          } catch (e) {
            const result = handleBackendError(e, setError, setErrorMessage);
            showErrorToast(e);
            resolve(result);
          }
        },
        (errors) => {
          console.error("Form errors:", errors);
          resolve(false);
        },
      )();
    });
  };

  return {
    control,
    admissionTypeOptions,
    statusTypeOptions,
    dependencyTypeOptions,
    errorMessage,
    submitForm,
    areRoomsLoading,
    areBedsLoading,
    formattedRoomData,
    formattedBedsData,
    attendingFor,
    selectedLocationId,
    selectedRoomId,
    validateRoomSelection,
    validateEndDate,
    setValue,
  };
};
